import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { BookOpen, Mail, ListChecks, CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";

const cards = [
  { key: "resources", label: "Total resources", icon: BookOpen, accent: "#4e2957" },
  { key: "resources_published", label: "Published", icon: CheckCircle2, accent: "#2eb7ab" },
  { key: "contacts", label: "Contact submissions", icon: Mail, accent: "#1a4c8e" },
  { key: "waitlist", label: "Waitlist signups", icon: ListChecks, accent: "#91a33a" },
];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api
      .get("/admin/stats")
      .then((r) => setStats(r.data))
      .finally(() => setLoading(false));
  }, []);

  const sendDigestPreview = async () => {
    if (!window.confirm("Send a preview of the weekly digest to your inbox now?")) return;
    setSending(true);
    try {
      const r = await api.post("/admin/digest/preview");
      toast.success(`Preview digest sent to ${r.data.sent_to}. Check your inbox.`);
    } catch { toast.error("Failed to send preview."); } finally { setSending(false); }
  };

  return (
    <div data-testid="admin-dashboard">
      <div className="eyebrow mb-2">Overview</div>
      <h1 className="display text-[36px] mb-10">Good to see you.</h1>

      {loading ? (
        <p className="text-brand-ink/50 text-[14px]">Loading…</p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {cards.map((c) => (
            <div
              key={c.key}
              data-testid={`stat-${c.key}`}
              className="relative rounded-lg border border-brand-rule/70 bg-brand-cream p-6"
            >
              <span className="absolute left-0 top-0 h-full w-[3px]" style={{ background: c.accent }} />
              <div className="flex items-center gap-3 text-brand-ink/60 text-[12px] uppercase tracking-wider">
                <c.icon size={14} /> {c.label}
              </div>
              <div className="mt-3 font-serif text-[40px] text-brand-ink">{stats?.[c.key] ?? 0}</div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-16 grid gap-6 sm:grid-cols-2 max-w-3xl">
        <div className="rounded-lg border border-brand-rule/70 bg-brand-cream p-6">
          <div className="font-serif text-[22px] text-brand-ink mb-2">Manage resources</div>
          <p className="text-[14px] text-brand-ink/70 mb-4">
            Add, edit, or archive articles, videos, and reflections shown on the
            Resources page.
          </p>
          <a href="/admin/resources" className="text-brand-blue text-[14px] hover:text-brand-ink">
            Open resources →
          </a>
        </div>
        <div className="rounded-lg border border-brand-rule/70 bg-brand-cream p-6">
          <div className="font-serif text-[22px] text-brand-ink mb-2">Review inbox</div>
          <p className="text-[14px] text-brand-ink/70 mb-4">
            Read incoming messages and export submissions as a CSV for your
            records.
          </p>
          <a href="/admin/contacts" className="text-brand-blue text-[14px] hover:text-brand-ink">
            Open inbox →
          </a>
        </div>
      </div>

      <div className="mt-8 rounded-lg border border-brand-rule/70 bg-brand-cream p-6 max-w-3xl">
        <div className="font-serif text-[22px] text-brand-ink mb-2">Weekly digest</div>
        <p className="text-[14px] text-brand-ink/70 mb-4">
          A calm Monday-morning summary of the past 7 days lands in your inbox at
          <span className="text-brand-ink"> 08:00 UTC</span> — grouped by experience, only when there's activity. Send yourself a preview to see the template.
        </p>
        <button
          onClick={sendDigestPreview}
          disabled={sending}
          data-testid="send-digest-preview"
          className="inline-flex items-center gap-2 rounded-full bg-brand-ink px-5 py-2.5 text-brand-cream text-[13.5px] hover:bg-brand-blue transition-colors disabled:opacity-60"
        >
          <Send size={13} /> {sending ? "Sending…" : "Send preview to my inbox"}
        </button>
      </div>
    </div>
  );
}
